import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import { fmtRelative } from "../../lib/utils-app";
import { Link } from "react-router-dom";
import { AlertTriangle, Sparkles } from "lucide-react";

const STATUSES = ["in_review", "draft", "approved", "rejected", "published", "all"];

export default function Drafts() {
  const [drafts, setDrafts] = useState([]);
  const [filter, setFilter] = useState("in_review");
  const [manual, setManual] = useState({ manual_title: "", manual_url: "", manual_text: "" });
  const [generating, setGenerating] = useState(false);

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/drafts${q}`).then((r) => setDrafts(r.data));
  };

  useEffect(() => { document.title = "Review Queue — IndiaODR Admin"; load(); /* eslint-disable-next-line */}, [filter]);

  const manualDraft = async (e) => {
    e.preventDefault(); setGenerating(true);
    try {
      const { data } = await api.post("/drafts/generate", manual);
      toast.success(`Draft generated: ${data.headline}`);
      setManual({ manual_title: "", manual_url: "", manual_text: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed."); }
    finally { setGenerating(false); }
  };

  const input = "border border-[var(--io-border)] focus:border-[var(--io-gold)] bg-[var(--io-bg-2)] px-3 py-2 rounded-md text-[13px] text-[var(--io-text)] placeholder:text-[var(--io-muted)] outline-none";

  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow" style={{ color: "#D4AF37" }}>AI Draft Review Queue</div>
          <h1 className="font-serif text-[32px] md:text-[40px] mt-1 text-[var(--io-text)]">Editorial review</h1>
          <p className="text-[12px] font-mono text-[var(--io-muted)] mt-1">{drafts.length} draft{drafts.length !== 1 ? "s" : ""} · {filter.replace("_"," ")}</p>
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="drafts-filter">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`io-badge ${filter === s ? "io-badge-gold" : ""}`} data-testid={`filter-${s}`}>{s.replace("_", " ")}</button>
          ))}
        </div>
      </div>

      <form onSubmit={manualDraft} className="io-card-el p-5 mb-8 grid md:grid-cols-12 gap-3" data-testid="manual-draft-form">
        <div className="md:col-span-12 eyebrow" style={{ color: "#D4AF37" }}>Draft from manual input</div>
        <input required placeholder="Headline or source title *" value={manual.manual_title} onChange={(e) => setManual({ ...manual, manual_title: e.target.value })} className={`md:col-span-5 ${input}`} data-testid="manual-title"/>
        <input placeholder="Source URL" value={manual.manual_url} onChange={(e) => setManual({ ...manual, manual_url: e.target.value })} className={`md:col-span-7 ${input}`} data-testid="manual-url"/>
        <textarea placeholder="Key facts / notes for the AI (optional)" rows={3} value={manual.manual_text} onChange={(e) => setManual({ ...manual, manual_text: e.target.value })} className={`md:col-span-12 ${input}`} data-testid="manual-text"/>
        <div className="md:col-span-12">
          <button disabled={generating} className="btn btn-primary" data-testid="manual-generate"><Sparkles size={14}/> {generating ? "Drafting…" : "Generate AI draft"}</button>
        </div>
      </form>

      <div className="io-card-el divide-y divide-[var(--io-border)]" data-testid="drafts-list">
        {drafts.length === 0 && <div className="p-10 text-[var(--io-muted)]" data-testid="drafts-empty">No drafts in this view.</div>}
        {drafts.map((d) => (
          <Link key={d.id} to={`/admin/drafts/${d.id}`} className="block p-5 group hover:bg-[var(--io-bg-2)]/60" data-testid={`draft-${d.id}`}>
            <div className="flex items-center gap-2 text-[11px] font-mono">
              <span className="text-[var(--io-gold)]">{d.category}</span>
              <span className="text-[var(--io-muted)]">·</span>
              <span className="text-[var(--io-text-2)]">{fmtRelative(d.updated_at)}</span>
              <span className="text-[var(--io-muted)]">·</span>
              <span className={d.ai_confidence >= 0.9 ? "text-[#10B981]" : d.ai_confidence >= 0.7 ? "text-[var(--io-gold)]" : "text-[#F59E0B]"}>AI {Math.round((d.ai_confidence || 0) * 100)}%</span>
            </div>
            <h3 className="font-serif text-[20px] md:text-[22px] mt-1.5 text-[var(--io-text)] group-hover:text-[var(--io-gold)]">{d.headline}</h3>
            {d.summary && <p className="mt-1.5 text-[13px] text-[var(--io-text-2)] line-clamp-2 max-w-[80ch]">{d.summary}</p>}
            <div className="flex flex-wrap gap-1.5 mt-3">
              <span className="io-badge">{d.status.replace("_", " ")}</span>
              <span className={`io-badge ${d.legal_risk === "high" ? "io-badge-red" : d.legal_risk === "medium" ? "io-badge-amber" : "io-badge-green"}`}>Legal: {d.legal_risk}</span>
              {d.sensitive_flags?.map((f) => (
                <span key={f} className="io-badge io-badge-amber"><AlertTriangle size={10}/> {f.replace(/_/g, " ")}</span>
              ))}
            </div>
          </Link>
        ))}
      </div>
    </AdminLayout>
  );
}
